// ============================================================================
// Stage labels
// ============================================================================
/**
 * Bare stage names. `BenchmarkDetail.tsx` and `reportMarkdown.ts` each carry
 * their own map already (the client's is prefixed "Stage 1: ...", the server's
 * is not) — those are left alone deliberately, since Joan's verbatim section
 * copy is written against them. New code uses these.
 */
export const BUYER_STAGE_LABELS = {
    problem_definition: 'Problem Definition',
    workflow_evaluation: 'Workflow Evaluation',
    technology_evaluation: 'Technology Evaluation',
    supplier_evaluation: 'Supplier Evaluation',
    supplier_selection: 'Supplier Selection',
};
/** Journey order, used to break "weakest stage" ties toward the later stage. */
export const BUYER_STAGE_ORDER = [
    'problem_definition',
    'workflow_evaluation',
    'technology_evaluation',
    'supplier_evaluation',
    'supplier_selection',
];
export function buyerStageLabel(stage, opts = {}) {
    const label = BUYER_STAGE_LABELS[stage] ?? stage;
    if (!opts.numbered)
        return label;
    const i = BUYER_STAGE_ORDER.indexOf(stage);
    return i >= 0 ? `Stage ${i + 1}: ${label}` : label;
}
// ============================================================================
// Small helpers
// ============================================================================
const pctStr = (v) => {
    if (!Number.isFinite(v))
        return '—';
    const p = v * 100;
    if (p > 0 && p < 1)
        return '<1%';
    return `${Math.round(p)}%`;
};
const visibleOf = (e) => e.visibleCount ?? Math.round((e.visibilityRate ?? 0) * (e.scoredCells ?? 0));
const modelVisibleOf = (m) => m.visible ?? Math.max(m.mention ?? 0, m.citation ?? 0, m.recommendation ?? 0);
/**
 * Minimum visibility before a conversion rate is meaningful.
 *
 * A company seen once that was not recommended reads as "0% conversion", which
 * is noise, not a finding — and it would rank a one-answer company against
 * Abcam's sixty-one. Companies below the floor are excluded from the
 * conversion ranking (never from the visibility ranking, where their low count
 * IS the point).
 */
export const CONVERSION_MIN_VISIBLE = 5;
// ============================================================================
// The derivations
// ============================================================================
export function findTarget(aggregate) {
    return aggregate.shareOfVoice?.entries?.find((e) => e.isTarget) ?? null;
}
/**
 * Visibility rank across every company in the run — named competitors AND the
 * companies AI surfaced unprompted.
 *
 * Deliberately ranks against BOTH sets. On the Antibodies Inc run the target
 * beats all five competitors it named while sitting tenth of fourteen overall,
 * so a rank computed against the named set alone would have flattered it and
 * hidden the real race (Abcam, CST, Bio-Techne).
 */
export function visibilityRankKpi(aggregate) {
    const entries = aggregate.shareOfVoice?.entries ?? [];
    const target = findTarget(aggregate);
    if (!target || entries.length === 0)
        return null;
    const ranked = [...entries].sort((a, b) => visibleOf(b) - visibleOf(a));
    const rank = ranked.findIndex((e) => e.company === target.company) + 1;
    const leader = ranked[0];
    if (rank === 0 || !leader)
        return null;
    const isLeader = leader.company === target.company;
    return {
        key: 'visibilityRank',
        label: 'Visibility rank',
        value: `#${rank} of ${ranked.length}`,
        detail: isLeader
            ? `Leads the field, visible in ${pctStr(target.visibilityRate)} of scored answers.`
            : `${leader.company} leads at ${pctStr(leader.visibilityRate)}; you are at ${pctStr(target.visibilityRate)}.`,
        tone: isLeader ? 'good' : rank <= 3 ? 'neutral' : 'bad',
        audience: 'client',
    };
}
/**
 * Recommendation conversion: of the answers where a company appears at all, how
 * many actually recommend it.
 *
 * This is the KPI that changes the recommendation. Visibility and conversion
 * are different problems with different fixes — on the Antibodies Inc run the
 * target converts at 50%, essentially level with Abcam (56%) and well ahead of
 * both distributors, while ranking tenth on reach. That is a reach problem, not
 * a reputation problem, and no table in the report said so.
 */
export function conversionKpi(aggregate) {
    const entries = aggregate.shareOfVoice?.entries ?? [];
    const target = findTarget(aggregate);
    if (!target)
        return null;
    const tVisible = visibleOf(target);
    const tRecs = target.recommendationsCount ?? 0;
    if (tVisible <= 0) {
        return {
            key: 'conversion',
            label: 'Recommendation conversion',
            value: '—',
            detail: 'Not visible in any scored answer, so conversion cannot be measured.',
            tone: 'bad',
            audience: 'client',
        };
    }
    const rate = tRecs / tVisible;
    const eligible = entries
        .filter((e) => visibleOf(e) >= CONVERSION_MIN_VISIBLE)
        .map((e) => ({
        company: e.company,
        isTarget: e.isTarget,
        rate: (e.recommendationsCount ?? 0) / Math.max(1, visibleOf(e)),
    }))
        .sort((a, b) => b.rate - a.rate);
    const rank = eligible.findIndex((e) => e.isTarget) + 1;
    const best = eligible.find((e) => !e.isTarget) ?? null;
    const detail = rank > 0
        ? `When AI names you, it recommends you ${tRecs} of ${tVisible} times — rank #${rank} of ${eligible.length}${best ? `, vs ${best.company} at ${pctStr(best.rate)}` : ''}.`
        : `Recommended in ${tRecs} of ${tVisible} answers where you appeared.`;
    return {
        key: 'conversion',
        label: 'Recommendation conversion',
        value: pctStr(rate),
        detail,
        tone: rate >= 0.5 ? 'good' : rate >= 0.25 ? 'neutral' : 'warn',
        audience: 'client',
    };
}
/**
 * How many panel models never surface the target at all.
 *
 * Defaults to `internal` — see `KpiAudience`. A blackout usually traces back to
 * the client's own crawler policy, which is a conversation, not a dashboard
 * tile.
 */
export function modelCoverageKpi(aggregate, audience = 'internal') {
    const models = aggregate.modelBreakdown?.entries ?? [];
    if (models.length === 0)
        return null;
    const blind = models.filter((m) => modelVisibleOf(m) === 0);
    const covered = models.length - blind.length;
    return {
        key: 'modelCoverage',
        label: 'Model coverage',
        value: `${covered} of ${models.length}`,
        detail: blind.length === 0
            ? 'Every model in the panel surfaced you at least once.'
            : `${blind.length === 1 ? 'One model never surfaced you' : `${blind.length} models never surfaced you`}: ${blind
                .map((m) => m.model)
                .join(', ')}.`,
        tone: blind.length === 0 ? 'good' : blind.length >= models.length / 2 ? 'bad' : 'warn',
        audience,
    };
}
/**
 * The target's share of the vendor-owned content AI cites.
 *
 * Supplier content is consistently the largest citation category (64% of all
 * citations on the Antibodies Inc run), which makes a company's own website the
 * single biggest available lever — and quantifies how little of it they hold
 * today (~0.8%, against Abcam's 13.8%).
 *
 * Returns null when the run captured no supplier-content citations (web-off
 * runs cite nothing) or when we know no domain for the target, rather than
 * printing a fabricated 0%.
 */
export function ownedContentKpi(aggregate) {
    const sources = aggregate.citationSources ?? [];
    const target = findTarget(aggregate);
    if (!target || sources.length === 0)
        return null;
    if (target.citedMeasurable === false)
        return null;
    const supplier = sources.find((s) => s.sourceType === 'Supplier Content');
    const supplierCitations = supplier?.citations ?? 0;
    if (!supplier || supplierCitations <= 0)
        return null;
    const totalCitations = sources.reduce((n, s) => n + (s.citations ?? 0), 0);
    const owned = target.sourceLinks ?? 0;
    const share = owned / supplierCitations;
    const categoryShare = totalCitations > 0 ? supplierCitations / totalCitations : 0;
    return {
        key: 'ownedContent',
        label: 'Share of vendor-cited content',
        value: pctStr(share),
        detail: `Vendor websites are ${pctStr(categoryShare)} of everything AI cited here (${supplierCitations} links). ${owned} of them are yours.`,
        tone: share >= 0.1 ? 'good' : share >= 0.03 ? 'neutral' : 'warn',
        audience: 'client',
    };
}
/**
 * The buyer-journey stage where the target is weakest.
 *
 * Ties break toward the LATER stage — the same decision-proximity rule the
 * recommendation leaders table already uses. Losing at supplier selection costs
 * more than losing at problem definition.
 */
export function weakestStageKpi(aggregate) {
    const stages = aggregate.stageVisibility?.entries ?? [];
    if (stages.length === 0)
        return null;
    const idx = (s) => BUYER_STAGE_ORDER.indexOf(s.stage);
    const weakest = [...stages].sort((a, b) => {
        if (a.visibilityRate !== b.visibilityRate)
            return a.visibilityRate - b.visibilityRate;
        return idx(b) - idx(a);
    })[0];
    const strongest = [...stages].sort((a, b) => b.visibilityRate - a.visibilityRate)[0];
    if (!weakest || !strongest)
        return null;
    return {
        key: 'weakestStage',
        label: 'Weakest journey stage',
        value: buyerStageLabel(weakest.stage),
        detail: `Visible in ${pctStr(weakest.visibilityRate)} of ${weakest.stage === strongest.stage ? 'answers' : `answers here, against ${pctStr(strongest.visibilityRate)} at your strongest (${buyerStageLabel(strongest.stage)})`}.`,
        tone: 'warn',
        audience: 'client',
    };
}
/**
 * Per-signal rankings — one panel per signal, every company ranked within it.
 *
 * This is Lisa's spec, verbatim (2026-09-01): "unless it's an entire mentions
 * chart and an entire citation chart aside, then you know who's winning in
 * citations, who's winning in mentions". The first pass grouped BY COMPANY,
 * which answers "what does this one company's mix look like" — a question
 * nobody asked — and made cross-company comparison impossible.
 *
 * The target is always present, appended below the cut when it does not rank,
 * because a competitive chart that omits the client is useless to the client.
 */
export function signalRankings(aggregate, limit = 8) {
    const entries = aggregate.shareOfVoice?.entries ?? [];
    if (entries.length === 0)
        return [];
    const defs = [
        { key: 'mention', label: 'Mentions', get: (e) => e.mentionsCount ?? 0 },
        { key: 'citation', label: 'Citations', get: (e) => e.citationsCount ?? 0 },
        {
            key: 'recommendation',
            label: 'Recommendations',
            get: (e) => e.recommendationsCount ?? 0,
        },
    ];
    return defs.map((d) => {
        const ranked = [...entries]
            .map((e) => ({
            company: e.company,
            value: d.get(e),
            isTarget: e.isTarget,
        }))
            .sort((a, b) => b.value - a.value);
        const rows = ranked.slice(0, limit);
        let targetAppended = false;
        if (!rows.some((r) => r.isTarget)) {
            const t = ranked.find((r) => r.isTarget);
            if (t) {
                rows.push(t);
                targetAppended = true;
            }
        }
        return { key: d.key, label: d.label, rows, targetAppended };
    });
}
/**
 * Source-type share of all citations — the donut Lisa asked for ("a pie chart
 * of all the source types to begin with: how much is supplier content? how much
 * is peer reviewed?").
 *
 * A pie is legitimate here specifically because each cited URL is classified
 * into exactly ONE source category, so the slices genuinely sum to a whole.
 * Capped at `maxSlices` with the tail folded into "Other" — part-to-whole
 * at a glance stops working past about six segments.
 */
export function sourceTypeShare(aggregate, maxSlices = 6) {
    const sources = aggregate.citationSources ?? [];
    const withCounts = sources
        .map((s) => ({
        label: String(s.sourceType),
        citations: s.citations ?? 0,
        domains: [...(s.domains ?? [])]
            .sort((a, b) => b.appearances - a.appearances ||
            (b.citations ?? 0) - (a.citations ?? 0))
            .slice(0, 5),
    }))
        .filter((s) => s.citations > 0)
        .sort((a, b) => b.citations - a.citations);
    const total = withCounts.reduce((n, s) => n + s.citations, 0);
    if (total <= 0)
        return [];
    const head = withCounts.slice(0, Math.max(1, maxSlices - 1));
    const tail = withCounts.slice(Math.max(1, maxSlices - 1));
    const slices = head.map((s) => ({
        label: s.label,
        citations: s.citations,
        share: s.citations / total,
        isOther: false,
        domains: s.domains,
    }));
    const tailTotal = tail.reduce((n, s) => n + s.citations, 0);
    if (tailTotal > 0) {
        slices.push({
            label: `Other (${tail.length})`,
            citations: tailTotal,
            share: tailTotal / total,
            isOther: true,
        });
    }
    return slices;
}
/**
 * The visibility funnel: every scored answer -> answers that surfaced the
 * company -> answers that recommended it.
 *
 * WHY THIS AND NOT A BAR OF THE THREE SIGNALS. Mention, Citation and
 * Recommendation overlap and do not nest (2026-07-13 D1), so they are not
 * funnel stages and drawing them as one would be a lie. But Visibility DOES
 * contain Recommendation, and "of all the answers in this category, how many
 * reached you, and of those how many endorsed you" is the actual commercial
 * question — and it localises the loss.
 *
 * On the Antibodies Inc run it reads: 104 -> 10 -> 5. Ninety percent of the
 * loss is at the first step and half is retained at the second, which is the
 * report's whole argument in one shape: a reach problem, not a reputation one.
 */
export function companyFunnel(entry, scoredAnswers) {
    const visible = entry.visibleCount ?? 0;
    const recommended = entry.recommendationsCount ?? 0;
    const raw = [
        { label: 'Answers in this category', value: scoredAnswers },
        { label: 'Answers that surfaced them', value: visible },
        { label: 'Answers that recommended them', value: recommended },
    ];
    const first = raw[0]?.value ?? 0;
    const steps = raw.map((s, i) => {
        const prev = i > 0 ? (raw[i - 1]?.value ?? 0) : null;
        return {
            label: s.label,
            value: s.value,
            ofTotal: first > 0 ? s.value / first : 0,
            retained: prev === null ? null : prev > 0 ? s.value / prev : 0,
        };
    });
    let biggestDropAt = 1;
    let worst = Infinity;
    steps.forEach((s, i) => {
        if (s.retained !== null && s.retained < worst) {
            worst = s.retained;
            biggestDropAt = i;
        }
    });
    return {
        company: entry.company,
        isTarget: entry.isTarget,
        tracked: entry.tracked === true && !entry.isTarget,
        steps,
        biggestDropAt,
    };
}
/**
 * Who the client's funnel can be drawn against.
 *
 * WHY THE DEFAULT CHANGED (Lisa, 2026-09-08). The first funnel compared the
 * client to the category leader — on the Antibodies Inc run, Thermo Fisher at
 * 68 answers against their 10. Lisa: "This is a perfect example of you versus
 * the top and nothing more ... it's just rubbing it in the face. Thermo wasn't
 * one of their named competitors." She was right on the facts as well as the
 * tone: within the set the client chose to be measured against (Synaptic
 * Systems 5, StressMarq 1, Alomone 0, Encor 0) they LEAD on every signal, and
 * the report never said so. So the funnel now opens on the strongest NAMED
 * competitor, and the category leader is one selection away — the reverse of
 * before. A run with no named set keeps the leader, the only comparison it has.
 *
 * Zero-visibility named competitors stay selectable: "AI never surfaced
 * Alomone Labs" is a finding, not missing data.
 */
export function funnelComparators(aggregate, maxLeaders = 5) {
    const entries = aggregate.shareOfVoice?.entries ?? [];
    const target = entries.find((e) => e.isTarget);
    if (!target)
        return { options: [], defaultCompany: null };
    const others = entries.filter((e) => !e.isTarget);
    const named = others
        .filter((e) => e.tracked === true)
        .sort((a, b) => visibleOf(b) - visibleOf(a) || a.company.localeCompare(b.company))
        .map((e) => ({ company: e.company, visible: visibleOf(e), group: 'named' }));
    const leaders = others
        .filter((e) => e.tracked !== true && visibleOf(e) > 0)
        .sort((a, b) => visibleOf(b) - visibleOf(a))
        .slice(0, maxLeaders)
        .map((e) => ({ company: e.company, visible: visibleOf(e), group: 'leader' }));
    const options = [...named, ...leaders];
    return {
        options,
        defaultCompany: named[0]?.company ?? leaders[0]?.company ?? null,
    };
}
/**
 * What the funnel's first band IS. Lisa, reading "104": "Is 104 the prompts?"
 * It is 26 prompts x 4 models, and the chart now says so. Prompts are summed
 * from the per-stage entries rather than derived as answers / models, which is
 * wrong whenever a cell failed to collect (2026-08-24).
 */
export function funnelContext(aggregate) {
    const entries = aggregate.shareOfVoice?.entries ?? [];
    const target = entries.find((e) => e.isTarget);
    const scoredAnswers = target?.scoredCells ?? entries[0]?.scoredCells ?? 0;
    if (scoredAnswers <= 0)
        return null;
    const models = aggregate.modelBreakdown?.entries?.length ?? 0;
    const summed = (aggregate.stageVisibility?.entries ?? []).reduce((n, s) => n + (s.prompts ?? 0), 0);
    const prompts = summed > 0 ? summed : models > 0 ? Math.round(scoredAnswers / models) : 0;
    return { scoredAnswers, prompts, models };
}
/**
 * The target's funnel beside a comparator's. Without an explicit `comparator`
 * the default from `funnelComparators` is used. Returns [] when there is no
 * target row to tell a story about.
 */
export function funnelComparison(aggregate, comparator) {
    const entries = aggregate.shareOfVoice?.entries ?? [];
    const target = entries.find((e) => e.isTarget);
    if (!target)
        return [];
    const scored = target.scoredCells ?? 0;
    if (scored <= 0)
        return [];
    const wanted = comparator ?? funnelComparators(aggregate).defaultCompany;
    const rival = wanted
        ? (entries.find((e) => !e.isTarget && e.company === wanted) ?? null)
        : null;
    const out = [companyFunnel(target, scored)];
    if (rival)
        out.push(companyFunnel(rival, rival.scoredCells ?? scored));
    return out;
}
/**
 * The sentence under the funnel. Lives here so `report.md` says exactly what
 * the screen says. When the client leads the comparison it says so plainly —
 * and names the category leader as the OTHER comparison rather than leading
 * with it.
 */
export function funnelVerdict(funnels, comparators) {
    const you = funnels.find((f) => f.isTarget);
    const rival = funnels.find((f) => !f.isTarget);
    if (!you || !rival)
        return null;
    const scored = you.steps[0]?.value ?? 0;
    const yourVisible = you.steps[1]?.value ?? 0;
    const yourRec = you.steps[2]?.value ?? 0;
    const rivalVisible = rival.steps[1]?.value ?? 0;
    const rivalRec = rival.steps[2]?.value ?? 0;
    const yourConv = you.steps[2]?.retained ?? 0;
    const rivalConv = rival.steps[2]?.retained ?? 0;
    const leader = comparators.options.find((o) => o.group === 'leader' && o.company !== rival.company) ?? null;
    const leaderNote = rival.tracked && leader && leader.visible > yourVisible
        ? ` The category leader, ${leader.company}, is surfaced in ${leader.visible} of ${scored} — switch the comparison to see that gap.`
        : '';
    const times = (n) => (n === 1 ? 'once' : `${n} times`);
    if (rivalVisible === 0) {
        return `AI never surfaced ${rival.company} in this benchmark. It surfaced you in ${yourVisible} of ${scored} answers and recommended you ${times(yourRec)}.${leaderNote}`;
    }
    if (yourVisible > rivalVisible) {
        const lead = rival.tracked
            ? 'Within your named competitive set you lead'
            : 'You lead this comparison';
        return `${lead}: AI surfaced you in ${yourVisible} answers against ${rival.company}'s ${rivalVisible}, and recommended you ${times(yourRec)} to their ${rivalRec}.${leaderNote}`;
    }
    if (yourVisible === 0) {
        return `AI surfaced ${rival.company} in ${rivalVisible} of ${scored} answers and never surfaced you — the whole gap is the first step.`;
    }
    if (yourVisible === rivalVisible) {
        return yourRec >= rivalRec
            ? `You and ${rival.company} are surfaced equally often (${yourVisible} answers each), and AI recommends you ${times(yourRec)} to their ${rivalRec}.`
            : `You and ${rival.company} are surfaced equally often (${yourVisible} answers each), but AI recommends them ${times(rivalRec)} to your ${yourRec} — the gap is the second step.`;
    }
    const ratio = rivalVisible / yourVisible;
    const ratioText = ratio >= 2 ? `${Math.round(ratio)}× as often` : 'more often';
    return yourConv >= rivalConv
        ? `${rival.company} is surfaced ${ratioText} as you, but once AI has surfaced you it recommends you at least as readily (${pctStr(yourConv)} against ${pctStr(rivalConv)}). The gap is the first step, not the second.`
        : `${rival.company} leads you on both steps — surfaced ${ratioText}, and recommended in ${pctStr(rivalConv)} of those answers against your ${pctStr(yourConv)}.`;
}
const median = (xs) => {
    if (xs.length === 0)
        return 0;
    const s = [...xs].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return s.length % 2 ? (s[mid] ?? 0) : ((s[mid - 1] ?? 0) + (s[mid] ?? 0)) / 2;
};
/**
 * Reach against conversion — the one chart that states the report's thesis.
 *
 * Two different problems hide inside a single visibility number: not being
 * SEEN, and being seen but not CHOSEN. They have opposite remedies, and no
 * ranked list can separate them because each is only one axis. Plotting both
 * puts every company in a quadrant and puts a verdict on the client.
 *
 * Antibodies Inc lands far left (reach 10%) and above the middle (conversion
 * 50%) — well-regarded but unseen. Abcam sits top-right. Fisher Scientific,
 * hugely visible at 11% conversion, sits bottom-right: seen everywhere and
 * recommended by almost no one.
 *
 * Only companies clearing `CONVERSION_MIN_VISIBLE` are plotted: a company seen
 * once and not recommended would sit at 0% conversion and read as a judgement
 * when it is really an absence of evidence.
 */
export function positioning(aggregate) {
    const entries = aggregate.shareOfVoice?.entries ?? [];
    const points = entries
        .filter((e) => (e.visibleCount ?? 0) >= CONVERSION_MIN_VISIBLE || e.isTarget)
        .map((e) => {
        const visible = e.visibleCount ?? 0;
        const recommended = e.recommendationsCount ?? 0;
        return {
            company: e.company,
            reach: e.visibilityRate ?? 0,
            conversion: visible > 0 ? recommended / visible : 0,
            visible,
            recommended,
            isTarget: e.isTarget,
            tracked: e.tracked ?? false,
        };
    });
    const scored = points.filter((p) => p.visible >= CONVERSION_MIN_VISIBLE);
    const reachMid = median(scored.map((p) => p.reach));
    const conversionMid = median(scored.map((p) => p.conversion));
    const target = points.find((p) => p.isTarget) ?? null;
    let targetQuadrant = null;
    if (target) {
        if (target.visible === 0)
            targetQuadrant = 'absent';
        else if (target.reach >= reachMid && target.conversion >= conversionMid)
            targetQuadrant = 'leader';
        else if (target.reach < reachMid && target.conversion >= conversionMid)
            targetQuadrant = 'well-regarded-but-unseen';
        else if (target.reach >= reachMid && target.conversion < conversionMid)
            targetQuadrant = 'seen-but-not-chosen';
        else
            targetQuadrant = 'absent';
    }
    return { points, reachMid, conversionMid, target, targetQuadrant };
}
/**
 * The race at every buyer-journey stage, WITH the client in it.
 *
 * WHY THIS REPLACES TWO CHARTS (Lisa, 2026-09-08). The bump chart drew the
 * leaders' race and the client was not on it — "this report isn't for Abcam
 * ... they're not even in the top five." The dumbbell that replaced it drew
 * the client against each stage's most-recommended company, and Lisa could
 * not read it: "it made a new data set and I don't understand it even more."
 * Its headline — "level with the leader at workflow evaluation" — rested on
 * one recommendation against one, which is noise wearing a verdict.
 *
 * What she DID read, instantly, was the per-stage leader grid: "this actually
 * answers the question that's missing above — where you sit against the
 * others." Ramos: "if we add that same information up into the race chart,
 * now we've got a visual and an explainer." This is that chart. Per stage:
 * the top five companies by visibility, and the client's own row drawn every
 * time — in place when they make the ladder, beneath it with their rank when
 * they do not. Visibility (the union) is the measure because it is the one
 * the stage chart Lisa liked already uses, and because it has the support to
 * rank on (9-20 leader answers per stage against recommendation counts as low
 * as 1).
 *
 * The recommendation is derived, not asserted: the stage where the fewest
 * extra answers would put the client level with the fifth-placed company is
 * the shortest path onto a leaderboard. On the Antibodies Inc run that is
 * Technology Evaluation — three answers — which is also the stage where AI
 * already recommends them most. Ties break toward the later, decision-proximal
 * stage, the convention `weakestStageKpi` already uses, so the ladder and the
 * KPI tile cannot disagree about which stage is weakest.
 */
export function stageStandings(aggregate, top = 5) {
    const empty = {
        top,
        stages: [],
        strongest: null,
        weakest: null,
        shortestPath: null,
        leaders: [],
        rival: null,
    };
    const entries = aggregate.stageVisibility?.entries ?? [];
    if (entries.length === 0)
        return empty;
    const targetName = findTarget(aggregate)?.company ?? null;
    const order = (s) => {
        const i = BUYER_STAGE_ORDER.indexOf(s);
        return i < 0 ? 99 : i;
    };
    const stages = [...entries]
        .sort((a, b) => order(a.stage) - order(b.stage))
        .map((s) => {
        const cells = s.scoredCells ?? 0;
        const isYou = (name) => targetName !== null && name === targetName;
        const rows = [...(s.surfaced ?? [])]
            .sort((a, b) => b.appearances - a.appearances ||
            b.recommendations - a.recommendations) // the aggregator's own order — ranks here match the surfaced list on screen
            .map((x, i) => ({
            company: x.company,
            count: x.appearances,
            rate: cells > 0 ? x.appearances / cells : 0,
            recommendations: x.recommendations,
            isTarget: isYou(x.company),
            tracked: x.tracked && !isYou(x.company),
            rank: i + 1,
        }));
        const youRow = rows.find((r) => r.isTarget) ?? null;
        // The stage entry always carries the client's own rate, even when the
        // capped surfaced list dropped them; the two agree whenever both exist.
        const youCount = youRow?.count ?? Math.round((s.visibilityRate ?? 0) * cells);
        const you = {
            count: youCount,
            rate: cells > 0 ? youCount / cells : 0,
            recommendations: s.recommendations ?? 0,
            rank: youRow?.rank ?? null,
            inTop: youRow !== null && youRow.rank <= top,
        };
        const ladder = rows.slice(0, top);
        const leader = ladder[0] ?? null;
        const cutoff = ladder.length > 0 ? (ladder[ladder.length - 1] ?? null) : null;
        const answersToCutoff = you.inTop || !cutoff ? null : Math.max(0, cutoff.count - you.count);
        return {
            stage: s.stage,
            label: buyerStageLabel(s.stage),
            scoredCells: cells,
            prompts: s.prompts ?? 0,
            ladder,
            you,
            listed: rows.length,
            leader,
            cutoff,
            answersToCutoff,
        };
    });
    const later = (a, b) => order(b.stage) - order(a.stage);
    const strongest = [...stages].sort((a, b) => b.you.rate - a.you.rate || later(a, b))[0] ??
        null;
    const weakest = [...stages].sort((a, b) => a.you.rate - b.you.rate || later(a, b))[0] ??
        null;
    const shortestPath = [...stages]
        .filter((s) => s.answersToCutoff !== null)
        .sort((a, b) => (a.answersToCutoff ?? 0) - (b.answersToCutoff ?? 0) || later(a, b))[0] ?? null;
    const tally = new Map();
    for (const s of stages) {
        if (!s.leader)
            continue;
        tally.set(s.leader.company, [
            ...(tally.get(s.leader.company) ?? []),
            s.stage,
        ]);
    }
    const leaders = [...tally.entries()]
        .map(([company, st]) => ({
        company,
        stages: st,
        isTarget: targetName !== null && company === targetName,
    }))
        .sort((a, b) => b.stages.length - a.stages.length || a.company.localeCompare(b.company));
    // IDT, 2026-10-22: the client led all five stages, so "the leader" was the
    // client and the report told it to hold its positions "against Integrated
    // DNA Technologies". The rival is chosen among everyone else.
    const bestOther = new Map();
    const answers = new Map();
    for (const s of stages) {
        const others = s.ladder.filter((r) => !r.isTarget);
        const first = others[0];
        if (first)
            bestOther.set(first.company, (bestOther.get(first.company) ?? 0) + 1);
        for (const r of others)
            answers.set(r.company, (answers.get(r.company) ?? 0) + r.count);
    }
    const rival = [...answers.keys()].sort((x, y) => (bestOther.get(y) ?? 0) - (bestOther.get(x) ?? 0) ||
        (answers.get(y) ?? 0) - (answers.get(x) ?? 0) ||
        x.localeCompare(y))[0] ?? null;
    return { top, stages, strongest, weakest, shortestPath, leaders, rival };
}
const ORDINAL_WORDS = [
    'first',
    'second',
    'third',
    'fourth',
    'fifth',
    'sixth',
    'seventh',
    'eighth',
    'ninth',
    'tenth',
];
const ordinalWord = (n) => ORDINAL_WORDS[n - 1] ?? `${n}th`;
/**
 * The two sentences under the standings chart — Ramos's "here's the visual,
 * here's what it's saying, and here's the recommendation". Shared so the
 * Markdown export states the same finding.
 */
export function stageStandingsVerdict(st) {
    const { strongest, weakest, shortestPath, leaders, stages, top, rival } = st;
    if (!strongest || !weakest)
        return null;
    const standing = (s) => s.you.rank !== null
        ? ` (#${s.you.rank} of the top ${s.listed})`
        : s.you.count === 0
            ? ' (never surfaced)'
            : ` (outside the top ${s.listed})`;
    const parts = [];
    if (strongest.stage === weakest.stage || stages.length === 1) {
        parts.push(`AI names you in ${strongest.you.count} of ${strongest.scoredCells} answers at ${strongest.label}${standing(strongest)}.`);
    }
    else {
        parts.push(`AI names you in ${strongest.you.count} of ${strongest.scoredCells} answers at your strongest stage, ${strongest.label}${standing(strongest)}, and in ${weakest.you.count} of ${weakest.scoredCells} at your weakest, ${weakest.label}${standing(weakest)}.`);
    }
    const [a, b] = leaders;
    if (a) {
        // The client is "you", in the second person, like every other sentence
        // here — never its own name in the third person.
        const lead = (t) => {
            const subject = t.isTarget ? 'you lead' : `${t.company} leads`;
            return t.stages.length === 1 && t.stages[0]
                ? `${subject} ${buyerStageLabel(t.stages[0])}`
                : `${subject} ${t.stages.length} of the ${stages.length} stages`;
        };
        const capitalised = (x) => x.charAt(0).toUpperCase() + x.slice(1);
        const peak = Math.max(0, ...stages
            .filter((s) => s.leader?.company === a.company)
            .map((s) => s.leader?.rate ?? 0));
        parts.push(`${capitalised(lead(a))}, named in up to ${pctStr(peak)} of a stage's answers${b ? `; ${lead(b)}` : ''}.`);
    }
    let doNext = null;
    if (shortestPath) {
        const n = shortestPath.answersToCutoff ?? 0;
        const place = ordinalWord(shortestPath.ladder.length);
        const step = n === 0
            ? `you are already level with the ${place}-placed company on answers there`
            : `${n} more ${n === 1 ? 'answer' : 'answers'} naming you would put you level with the ${place}-placed company`;
        doNext = `Start at ${shortestPath.label}: ${step} — the shortest path onto a stage leaderboard in this benchmark.`;
        if (shortestPath.you.recommendations > 0) {
            doNext += ` AI already recommends you there in ${shortestPath.you.recommendations} of ${shortestPath.scoredCells} answers, so the case is made once you are surfaced.`;
        }
    }
    else if (stages.length > 0 && stages.every((s) => s.you.inTop)) {
        doNext = `You already place in the top ${top} at every stage. The work is holding those positions${rival ? ` against ${rival}` : ''}.`;
    }
    return { shows: parts.join(' '), doNext };
}
/**
 * The scopes a run supports. 'named' is offered only when the client actually
 * listed competitors — an empty competitive-set pie would be a chart of one.
 */
export function signalShareScopes(aggregate) {
    const hasNamed = (aggregate.shareOfVoice?.entries ?? []).some((e) => e.tracked === true && !e.isTarget);
    return [
        { key: 'all', label: 'Everyone AI named' },
        { key: 'leaders', label: 'Top companies only' },
        ...(hasNamed
            ? [{ key: 'named', label: 'Your named competitive set' }]
            : []),
    ];
}
const SHARE_DEFS = [
    { key: 'mention', label: 'Mentions' },
    { key: 'citation', label: 'Citations' },
    { key: 'recommendation', label: 'Recommendations' },
];
/**
 * Share of voice per signal, as part-to-whole — in one of three scopes.
 *
 * A pie needs a real whole. Each mention is one (answer, company) event and
 * belongs to exactly one company, so these genuinely compose.
 *
 * SCOPES (Lisa + Ramos, 2026-09-08). The honest denominator is EVERY company
 * AI named — on the Antibodies Inc run 241 companies and 881 mentions, of
 * which the share-of-voice table's top-N holds 358; a pie over the table would
 * overstate every slice ~2.5x. That view shows a 70% "Other" and Lisa's first
 * question was "who's everyone else?" Ramos asked for the inverse — just the
 * top tier, where Thermo is the big slice — and Lisa for a third: "focus it on
 * their specific competitors." All three are offered; each states its own
 * denominator; the default stays the honest one.
 *
 * ONE HEAD FOR ALL THREE PIES. The first version let each pie pick its own
 * top five in its own order, so Thermo Fisher was orange in Mentions and green
 * in Recommendations — the colour drift Lisa caught ("consistent in colour,
 * because if we do a deck we'll be using the same"). The head is now the five
 * companies ranking highest across the three signals together, in one fixed
 * order, so a colour follows the company and the three pies are directly
 * comparable wedge by wedge.
 */
export function signalShares(aggregate, opts = {}) {
    const scope = opts.scope ?? 'all';
    const maxNamed = opts.maxNamed ?? 5;
    const all = aggregate.surfacedSuppliers ?? [];
    if (all.length === 0)
        return [];
    const sov = aggregate.shareOfVoice?.entries ?? [];
    const targetName = sov.find((e) => e.isTarget)?.company ??
        all.find((s) => s.isTarget)?.company ??
        null;
    const isTargetRow = (s) => s.isTarget === true || (targetName !== null && s.company === targetName);
    const rows = all.map((s) => ({
        company: s.company,
        isTarget: isTargetRow(s),
        values: {
            mention: s.mentions ?? 0,
            citation: s.citations ?? 0,
            recommendation: s.recommendations ?? 0,
        },
    }));
    const zero = {
        mention: 0,
        citation: 0,
        recommendation: 0,
    };
    const targetRow = rows.find((r) => r.isTarget) ?? {
        company: targetName ?? 'You',
        isTarget: true,
        values: zero,
    };
    let head;
    if (scope === 'named') {
        head = sov
            .filter((e) => e.tracked === true && !e.isTarget)
            .sort((a, b) => visibleOf(b) - visibleOf(a) || a.company.localeCompare(b.company))
            .map((e) => rows.find((r) => r.company === e.company) ?? {
            company: e.company,
            isTarget: false,
            values: zero,
        });
        if (head.length === 0)
            return [];
    }
    else {
        const others = rows.filter((r) => !r.isTarget);
        const bestRank = new Map();
        for (const d of SHARE_DEFS) {
            others
                .filter((r) => r.values[d.key] > 0)
                .sort((a, b) => b.values[d.key] - a.values[d.key] ||
                a.company.localeCompare(b.company))
                .forEach((r, i) => {
                const prev = bestRank.get(r.company) ?? Infinity;
                if (i + 1 < prev)
                    bestRank.set(r.company, i + 1);
            });
        }
        head = others
            .filter((r) => bestRank.has(r.company))
            .sort((a, b) => (bestRank.get(a.company) ?? 99) - (bestRank.get(b.company) ?? 99) ||
            b.values.mention - a.values.mention ||
            a.company.localeCompare(b.company))
            .slice(0, maxNamed)
            // Display order by mentions: the biggest wedge follows the client, in
            // the order the reader already saw in the visibility table.
            .sort((a, b) => b.values.mention - a.values.mention ||
            a.company.localeCompare(b.company));
    }
    const headKeys = new Set(head.map((r) => r.company));
    return SHARE_DEFS.map((d) => {
        const val = (r) => r.values[d.key];
        const inScope = scope === 'all' ? rows : [targetRow, ...head];
        const total = inScope.reduce((n, r) => n + val(r), 0);
        const companies = inScope.filter((r) => val(r) > 0).length;
        const mk = (r, tracked) => ({
            company: r.company,
            value: val(r),
            share: total > 0 ? val(r) / total : 0,
            isTarget: r.isTarget,
            isOther: false,
            tracked,
        });
        const slices = [
            mk(targetRow, false),
            ...head.map((r) => mk(r, scope === 'named')),
        ];
        let otherMax = 0;
        if (scope === 'all') {
            const tail = rows.filter((r) => !r.isTarget && !headKeys.has(r.company) && val(r) > 0);
            const tailTotal = tail.reduce((n, r) => n + val(r), 0);
            otherMax = tail.reduce((m, r) => Math.max(m, total > 0 ? val(r) / total : 0), 0);
            if (tailTotal > 0) {
                slices.push({
                    company: `Other (${tail.length} companies)`,
                    value: tailTotal,
                    share: tailTotal / total,
                    isTarget: false,
                    isOther: true,
                    tracked: false,
                    companies: tail.length,
                });
            }
        }
        const target = slices.find((s) => s.isTarget) ?? null;
        return {
            key: d.key,
            label: d.label,
            slices,
            total,
            companies,
            target,
            scope,
            otherMax,
        };
    }).filter((s) => s.total > 0);
}
/**
 * Who is most-named in a pie. IDT, 2026-10-22: the dashboard called GenScript
 * (7.8%) "the most-named company" while IDT itself held 11%, because the
 * caption took the largest slice EXCLUDING the client. The leader is chosen
 * over every named slice; "Other" is a fold of many companies, never one.
 */
export function signalShareLeader(share) {
    const named = share.slices
        .filter((x) => !x.isOther && x.value > 0)
        .sort((a, b) => b.value - a.value || Number(b.isTarget) - Number(a.isTarget));
    const top = named[0];
    if (!top)
        return null;
    const next = named[1] ?? null;
    const tied = next !== null && next.value === top.value;
    return {
        top,
        next,
        rival: named.find((x) => !x.isTarget) ?? null,
        youLead: top.isTarget && !tied,
        tied,
    };
}
/**
 * Each model's citation mix, expressed in the SAME category order as the
 * run-wide donut.
 *
 * Lisa's open question (2026-09-01), after asking for the overall pie: "but then
 * my question is if we open it, do we then want one for each one as well?" —
 * this is the "one for each one".
 *
 * The category ORDER is taken from the run-wide mix and applied to every model,
 * which is what lets a colour mean the same thing in every bar and in the donut
 * above them. Ordering each model by its own ranking would repaint the
 * categories per row — the recolour-on-filter anti-pattern.
 */
export function sourceMixByModel(aggregate, maxSlices = 6) {
    const order = sourceTypeShare(aggregate, maxSlices);
    if (order.length === 0)
        return [];
    const named = order.filter((s) => !s.isOther).map((s) => s.label);
    const otherLabel = order.find((s) => s.isOther)?.label ?? null;
    const byModel = aggregate.citationSourcesByModel ?? [];
    return byModel
        .map((m) => {
        const sources = m.sources ?? [];
        // Prefer real citation volume; fall back to appearances, which is what
        // aggregates actually store per model. See ModelSourceMix.basis.
        const hasCitations = sources.some((s) => (s.citations ?? 0) > 0);
        const basis = hasCitations
            ? 'citations'
            : 'appearances';
        const valueOf = (s) => basis === 'citations' ? (s.citations ?? 0) : (s.appearances ?? 0);
        const counts = new Map();
        let total = 0;
        for (const s of sources) {
            const c = valueOf(s);
            if (c <= 0)
                continue;
            total += c;
            const key = named.includes(String(s.sourceType))
                ? String(s.sourceType)
                : otherLabel;
            if (key)
                counts.set(key, (counts.get(key) ?? 0) + c);
        }
        if (total <= 0)
            return { model: m.model, slices: [], totalCitations: 0, basis };
        const slices = [];
        for (const label of named) {
            const c = counts.get(label) ?? 0;
            slices.push({
                label,
                citations: c,
                share: c / total,
                isOther: false,
            });
        }
        if (otherLabel) {
            const c = counts.get(otherLabel) ?? 0;
            slices.push({
                label: otherLabel,
                citations: c,
                share: c / total,
                isOther: true,
            });
        }
        return { model: m.model, slices, totalCitations: total, basis };
    })
        .filter((m) => m.totalCitations > 0);
}
/**
 * Per-stage comparison: the target against its best-performing named competitor
 * at each buyer-journey stage.
 *
 * Lisa: "definitely by journey stage... show how Antibodies Inc does against
 * its named competitor set at stage one, and then one for stage two."
 */
export function stageComparison(aggregate) {
    const stages = aggregate.stageVisibility?.entries ?? [];
    return stages.map((s) => {
        const competitors = [...(s.competitors ?? [])].sort((a, b) => b.visibilityRate - a.visibilityRate);
        const top = competitors[0];
        return {
            stage: s.stage,
            label: buyerStageLabel(s.stage),
            targetRate: s.visibilityRate ?? 0,
            targetRecommendations: s.recommendations ?? 0,
            prompts: s.prompts ?? 0,
            // A tracked competitor that scored ZERO here is kept, deliberately: "no
            // named competitor was visible at this stage" is a finding (the client
            // owns the stage), and dropping it renders an empty slot that reads as
            // missing data instead. Only a stage with no tracked competitors at all
            // yields null.
            topCompetitor: top
                ? { company: top.company, rate: top.visibilityRate ?? 0 }
                : null,
        };
    });
}
/**
 * The whole set, in the order the report leads with them.
 *
 * `internalKpis` controls whether audience-restricted tiles (currently just
 * model coverage) are included — pass true for the internal view, false for the
 * client view.
 */
export function deriveBenchmarkKpis(aggregate, opts = {}) {
    const empty = {
        kpis: [],
        signalRankings: [],
        sourceShare: [],
        stageComparison: [],
        target: null,
    };
    if (!aggregate)
        return empty;
    const candidates = [
        visibilityRankKpi(aggregate),
        conversionKpi(aggregate),
        ownedContentKpi(aggregate),
        weakestStageKpi(aggregate),
        modelCoverageKpi(aggregate, opts.internalKpis ? 'internal' : 'internal'),
    ].filter((k) => k !== null);
    const kpis = candidates.filter((k) => k.audience === 'client' || opts.internalKpis === true);
    return {
        kpis,
        signalRankings: signalRankings(aggregate),
        sourceShare: sourceTypeShare(aggregate),
        stageComparison: stageComparison(aggregate),
        target: findTarget(aggregate),
    };
}
//# sourceMappingURL=reportKpis.js.map